/**
 * useFinancialsTabState - Hook for managing financials tab state
 *
 * Extracted from CompanyDetails.jsx to reduce file size and improve maintainability.
 * Manages all state related to the Financials tab:
 * - Scope the tab opens on (consolidated/individual); FinancialsTab owns the live selection
 * - Financial level selection per group
 * - Financial details collapse state
 * - Financial filters toggle state
 * - Financial years
 * - Row heights for table layout
 * - Filtered financial groups (computed)
 * - Download toggle state
 *
 * @module useFinancialsTabState
 */

import { useLocalObservable } from 'mobx-react';

/**
 * Hook for managing financials tab state
 *
 * @param {Object} deps - Dependencies
 * @param {Function} deps.getFinancials - Getter for financials data
 * @returns {Object} Observable state object
 */
const useFinancialsTabState = ({ getFinancials }) => {
  const state = useLocalObservable(() => ({
    // ============================================
    // Initial Scope
    // ============================================
    // Set from the details response. It is where the Financials tab opens; the tab the
    // user switches to afterwards lives in FinancialsTab and is not mirrored here.
    initialFinancialsScope: 'consolidated',

    // ============================================
    // Level Selection
    // ============================================
    selectedFinancialLevelsConsolidated: {},
    selectedFinancialLevelsIndividual: {},

    setFinancialLevelConsolidated: (id, level) => {
      state.selectedFinancialLevelsConsolidated[id] = level;
      state.toggleFinancialFilterConsolidated(id);
    },

    setFinancialLevelIndividual: (id, level) => {
      state.selectedFinancialLevelsIndividual[id] = level;
      state.toggleFinancialFilterIndividual(id);
    },

    // ============================================
    // Collapsed State
    // ============================================
    collapsedFinancialDetailsConsolidated: {},
    collapsedFinancialDetailsIndividual: {},

    toggleFinancialDetailsConsolidated: (id) => {
      state.collapsedFinancialDetailsConsolidated[id] =
        !state.collapsedFinancialDetailsConsolidated[id];
      if (state.collapsedFinancialDetailsConsolidated[id]) {
        return;
      }

      setTimeout(() => {
        const rightFinancialContentDOM = document.getElementById(`right-financial-content-${id}`);
        if (rightFinancialContentDOM && rightFinancialContentDOM.scrollWidth) {
          rightFinancialContentDOM.scrollTo({
            left: rightFinancialContentDOM.scrollWidth,
            behavior: 'smooth'
          });
        }
      }, 10);
    },

    toggleFinancialDetailsIndividual: (id) => {
      state.collapsedFinancialDetailsIndividual[id] =
        !state.collapsedFinancialDetailsIndividual[id];
      if (state.collapsedFinancialDetailsIndividual[id]) {
        return;
      }

      setTimeout(() => {
        const rightFinancialContentDOM = document.getElementById(`right-financial-content-${id}`);
        if (rightFinancialContentDOM && rightFinancialContentDOM.scrollWidth) {
          rightFinancialContentDOM.scrollTo({
            left: rightFinancialContentDOM.scrollWidth,
            behavior: 'smooth'
          });
        }
      }, 10);
    },

    // ============================================
    // Filters Toggle
    // ============================================
    toggledFinancialFiltersConsolidated: {},
    toggledFinancialFiltersIndividual: {},

    toggleFinancialFilterConsolidated: (id) => {
      state.toggledFinancialFiltersConsolidated[id] =
        !state.toggledFinancialFiltersConsolidated[id];
    },

    toggleFinancialFilterIndividual: (id) => {
      state.toggledFinancialFiltersIndividual[id] = !state.toggledFinancialFiltersIndividual[id];
    },

    // ============================================
    // Financial Years
    // ============================================
    financialYearsConsolidated: [],
    financialYearsIndividual: [],

    setFinancialYearsConsolidated: (years = []) => {
      state.financialYearsConsolidated = years;
    },

    setFinancialYearsIndividual: (years = []) => {
      state.financialYearsIndividual = years;
    },

    // ============================================
    // Row Heights
    // ============================================
    financialTablesFirstRowsHeights: {},
    financialTablesRowsHeights: {},

    setFinancialTableFirstRowHeight: (groupId, height) => {
      if (state.financialTablesFirstRowsHeights[groupId] !== height) {
        state.financialTablesFirstRowsHeights[groupId] = height;
      }
    },

    setFinancialTableRowHeight: (groupId, itemName, height) => {
      if (state.financialTablesRowsHeights[groupId]?.[itemName] !== height) {
        if (!state.financialTablesRowsHeights[groupId]) {
          state.financialTablesRowsHeights[groupId] = {};
        }
        state.financialTablesRowsHeights[groupId][itemName] = height;
      }
    },

    // ============================================
    // Download Toggle
    // ============================================
    toggledFinancialDownload: false,
    toggleFinancialDownload: () => {
      state.toggledFinancialDownload = !state.toggledFinancialDownload;
    },

    // ============================================
    // Computed: Filtered Financial Groups
    // ============================================
    get filteredFinancialGroupsConsolidated() {
      const financials = getFinancials();
      const groups = [];
      financials?.details?.consolidated?.groups?.forEach?.((group) => {
        const groupCopy = { ...group };
        groups.push(groupCopy);
        groupCopy.items = group.items.filter(
          (item) =>
            item.level <=
            (state.selectedFinancialLevelsConsolidated?.[group.id] ||
              group.levels[group.levels.length - 1] ||
              1)
        );
      });
      return groups;
    },

    get filteredFinancialGroupsIndividual() {
      const financials = getFinancials();
      const groups = [];
      financials?.details?.individual?.groups?.forEach?.((group) => {
        const groupCopy = { ...group };
        groups.push(groupCopy);
        groupCopy.items = group.items.filter(
          (item) =>
            item.level <=
            (state.selectedFinancialLevelsIndividual?.[group.id] ||
              group.levels[group.levels.length - 1] ||
              1)
        );
      });
      return groups;
    },

    // ============================================
    // Initialize From API Response
    // ============================================
    initializeFromResponse: (response) => {
      // Set years
      state.financialYearsConsolidated = response.details.consolidated.detailsYears;
      state.financialYearsIndividual = response.details.individual.detailsYears;

      // Set the scope the tab opens on
      state.initialFinancialsScope = response.initialIndividualView ? 'individual' : 'consolidated';

      // Initialize consolidated groups
      response.details.consolidated.groups.forEach((group) => {
        state.selectedFinancialLevelsConsolidated[group.id] = group.levels.includes(2)
          ? 2
          : group.levels[group.levels.length - 1];
        state.collapsedFinancialDetailsConsolidated[group.id] = false;
        state.toggledFinancialFiltersConsolidated[group.id] = false;
      });

      // Initialize individual groups
      response.details.individual.groups.forEach((group) => {
        state.selectedFinancialLevelsIndividual[group.id] = group.levels.includes(2)
          ? 2
          : group.levels[group.levels.length - 1];
        state.collapsedFinancialDetailsIndividual[group.id] = false;
        state.toggledFinancialFiltersIndividual[group.id] = false;
      });
    }
  }));

  return state;
};

export default useFinancialsTabState;
